import { useEffect, useState } from "react";
import Topbar from "../components/layout/Topbar.jsx";
import { Pagination } from "../components/ui/index.jsx";
import { useNotifications } from "../context/NotificationContext.jsx";
import {
  getAllNotifications,
  markAsRead,
  markAllAsRead,
} from "../services/notificationService";

// Nombre de notifications affichées par page (pagination côté client, comme
// pour ClientsPage — la liste complète est déjà chargée en un seul appel).
const PAGE_SIZE = 15;

// ─────────────────────────────────────────────────────────────────────────────
//  Mapping NotificationType (backend) → icône + couleur
//  Le backend envoie : INFO | WARNING | SUCCESS | ERROR
// ─────────────────────────────────────────────────────────────────────────────
const TYPE_STYLE = {
  INFO:    { icon: "bi-info-circle",          color: "teal",   label: "Information"   },
  WARNING: { icon: "bi-exclamation-triangle", color: "orange", label: "Avertissement" },
  SUCCESS: { icon: "bi-check2-circle",        color: "green",  label: "Succès"        },
  ERROR:   { icon: "bi-x-circle",             color: "red",    label: "Erreur"        },
};

const COLOR_MAP = { teal: "var(--accent)", green: "var(--success)", orange: "var(--accent2)", red: "var(--danger)" };
const BG_MAP    = { teal: "var(--accent-dim)", green: "var(--success-dim)", orange: "var(--accent2-dim)", red: "var(--danger-dim)" };

function timeAgo(isoDate) {
  if (!isoDate) return "";
  const diff = Math.floor((Date.now() - new Date(isoDate)) / 1000 / 60);
  if (diff < 1)    return "à l'instant";
  if (diff < 60)   return `il y a ${diff}min`;
  if (diff < 1440) return `il y a ${Math.floor(diff / 60)}h`;
  return `il y a ${Math.floor(diff / 1440)}j`;
}

function fullDate(isoDate) {
  if (!isoDate) return "";
  return new Date(isoDate).toLocaleString("fr-CA", {
    dateStyle: "long",
    timeStyle: "short",
  });
}

// ─────────────────────────────────────────────────────────────────────────────
//  MODALE DE DÉTAIL — s'ouvre au clic sur une notification
// ─────────────────────────────────────────────────────────────────────────────
function NotificationDetailModal({ notif, onClose }) {
  const style = TYPE_STYLE[notif.type] ?? TYPE_STYLE.INFO;

  return (
    <div
      onClick={e => e.target === e.currentTarget && onClose()}
      style={{
        position: "fixed", inset: 0, zIndex: 1055,
        background: "rgba(26,26,26,.6)", backdropFilter: "blur(4px)",
        display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
        animation: "notifOverlayIn .15s ease",
      }}
    >
      <div style={{
        background: "var(--surface, #fff)", border: "1px solid var(--border)",
        borderRadius: 20, boxShadow: "0 40px 120px rgba(0,0,0,.28)",
        width: "100%", maxWidth: 440, overflow: "hidden",
        animation: "notifBoxIn .22s cubic-bezier(.34,1.56,.64,1)",
      }}>
        {/* En-tête coloré selon le type, en dégradé pour rester léger */}
        <div style={{
          padding: "30px 26px 22px", textAlign: "center", position: "relative",
          background: `linear-gradient(180deg, ${BG_MAP[style.color]}, transparent)`,
        }}>
          <button
            onClick={onClose}
            style={{
              position: "absolute", top: 14, right: 14,
              background: "rgba(0,0,0,.06)", border: "none", borderRadius: 8,
              width: 30, height: 30, cursor: "pointer", color: "var(--text-2)",
              display: "flex", alignItems: "center", justifyContent: "center",
              transition: "background .15s",
            }}
          >
            <i className="bi bi-x-lg" style={{ fontSize: 13 }} />
          </button>

          {/* Icône avec halo pour bien la détacher du dégradé */}
          <div style={{
            width: 60, height: 60, borderRadius: "50%", margin: "0 auto 14px",
            background: BG_MAP[style.color], color: COLOR_MAP[style.color],
            display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24,
            boxShadow: "0 0 0 6px var(--surface, #fff)",
          }}>
            <i className={`bi ${style.icon}`} />
          </div>

          <span style={{
            display: "inline-block", fontSize: 10.5, fontWeight: 700, letterSpacing: ".05em",
            textTransform: "uppercase", color: COLOR_MAP[style.color],
            background: "var(--surface, #fff)", borderRadius: 20, padding: "4px 12px", marginBottom: 12,
          }}>
            {style.label}
          </span>

          <div style={{ fontSize: 16, fontWeight: 700, color: "var(--text-1)", lineHeight: 1.4, padding: "0 8px" }}>
            {notif.title}
          </div>
          <div style={{ fontSize: 12, color: "var(--text-3)", marginTop: 6 }}>
            {fullDate(notif.createdAt)}
          </div>
        </div>

        {/* Message complet */}
        <div style={{ padding: "20px 26px", borderTop: "1px solid var(--border)" }}>
          {notif.message ? (
            <p style={{ fontSize: 13, color: "var(--text-2)", lineHeight: 1.7, margin: 0, whiteSpace: "pre-wrap" }}>
              {notif.message}
            </p>
          ) : (
            <p style={{ fontSize: 13, color: "var(--text-3)", fontStyle: "italic", margin: 0, textAlign: "center" }}>
              Aucun détail supplémentaire.
            </p>
          )}
        </div>

        <div style={{ padding: "0 26px 22px" }}>
          <button
            onClick={onClose}
            className="btn-itm btn-itm-primary"
            style={{ width: "100%", justifyContent: "center" }}
          >
            Fermer
          </button>
        </div>
      </div>

      <style>{`
        @keyframes notifOverlayIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes notifBoxIn {
          from { opacity: 0; transform: translateY(16px) scale(.97); }
          to   { opacity: 1; transform: none; }
        }
      `}</style>
    </div>
  );
}

export default function NotificationsPage() {

  const [notifs,  setNotifs]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [filter,  setFilter]  = useState("all"); // "all" | "unread"
  const [page,    setPage]    = useState(0);

  // Notification actuellement ouverte dans la popup de détail (null = fermée)
  const [viewNotif, setViewNotif] = useState(null);

  // Fonctions du contexte pour synchroniser Topbar + Sidebar en temps réel
  const { decrementUnread, resetUnread } = useNotifications();

  // ── Chargement initial ────────────────────────────────────────────────
  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const res = await getAllNotifications();
        setNotifs(res.data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // ── Clic sur une notification : ouvre le détail + marque comme lue ─────
  const handleOpen = async (notif) => {
    setViewNotif(notif);
    if (notif.read) return;
    try {
      await markAsRead(notif.id);
      setNotifs(prev => prev.map(n => n.id === notif.id ? { ...n, read: true } : n));
      decrementUnread(1); // ← met à jour le badge Topbar/Sidebar immédiatement
    } catch (err) {
      console.error(err.message);
    }
  };

  // ── Marquer toutes les notifs comme lues ──────────────────────────────
  const handleMarkAllAsRead = async () => {
    try {
      await markAllAsRead();
      setNotifs(prev => prev.map(n => ({ ...n, read: true })));
      resetUnread(); // ← remet le badge à 0 dans Topbar/Sidebar immédiatement
    } catch (err) {
      console.error(err.message);
    }
  };

  const unreadCount     = notifs.filter(n => !n.read).length;
  const displayedNotifs = filter === "unread" ? notifs.filter(n => !n.read) : notifs;
  const paginatedNotifs = displayedNotifs.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  // Revenir à la première page quand on change de filtre — sinon on peut se
  // retrouver sur une page qui n'existe plus (ex: filtre "Non lues" avec
  // moins de résultats que la page courante).
  const handleFilterChange = (key) => {
    setFilter(key);
    setPage(0);
  };

  if (loading) return (
    <>
      <Topbar title="Mes" subtitle="notifications" />
      <div className="page-body" style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 300 }}>
        <span style={{ color: "var(--text-3)", fontSize: 14 }}>Chargement…</span>
      </div>
    </>
  );

  if (error) return (
    <>
      <Topbar title="Mes" subtitle="notifications" />
      <div className="page-body" style={{ color: "var(--danger)", padding: 20 }}>{error}</div>
    </>
  );

  return (
    <>
      <Topbar title="Mes" subtitle="notifications" />
      <div className="page-body">

        {/* ── En-tête ── */}
        <div className="page-header">
          <div>
            <div className="page-title">
              Notifications{" "}
              {unreadCount > 0 && (
                <span style={{ fontSize: 14, color: "var(--accent)", fontWeight: 400 }}>
                  ({unreadCount} nouvelles)
                </span>
              )}
            </div>
          </div>
          <div className="page-actions">
            {unreadCount > 0 && (
              <button className="btn-itm btn-itm-outline" onClick={handleMarkAllAsRead}>
                <i className="bi bi-check-all" /> Tout marquer comme lu
              </button>
            )}
          </div>
        </div>

        {/* ── Filtres ── */}
        <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
          {[
            { key: "all",    label: "Toutes" },
            { key: "unread", label: `Non lues (${unreadCount})` },
          ].map(f => (
            <button
              key={f.key}
              onClick={() => handleFilterChange(f.key)}
              style={{
                padding: "5px 14px", borderRadius: 20, fontSize: 12,
                border: "1px solid var(--border)", cursor: "pointer",
                background: filter === f.key ? "var(--accent-dim)" : "transparent",
                color:      filter === f.key ? "var(--accent)"     : "var(--text-3)",
                fontWeight: filter === f.key ? 600 : 400, transition: "all .15s",
              }}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* ── Liste ── */}
        <div className="itm-card anim-fade-up">
          {displayedNotifs.length === 0 ? (
            <div className="empty-state">
              <i className="bi bi-bell-slash" />
              <p>{filter === "unread" ? "Aucune notification non lue." : "Aucune notification."}</p>
            </div>
          ) : (
            paginatedNotifs.map((n, i) => {
              const style = TYPE_STYLE[n.type] ?? TYPE_STYLE.INFO;
              return (
                <div
                  key={n.id}
                  onClick={() => handleOpen(n)}
                  style={{
                    display: "flex", gap: 14, padding: "14px 0",
                    borderBottom: i < paginatedNotifs.length - 1 ? "1px solid var(--border)" : "none",
                    opacity: n.read ? 0.7 : 1,
                    cursor: "pointer",
                    background: !n.read ? "rgba(0,194,203,.02)" : "transparent",
                    transition: "all .18s",
                  }}
                >
                  <div style={{ width: 40, height: 40, borderRadius: 12, background: BG_MAP[style.color], color: COLOR_MAP[style.color], display: "flex", alignItems: "center", justifyContent: "center", fontSize: 17, flexShrink: 0 }}>
                    <i className={`bi ${style.icon}`} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontWeight: n.read ? 400 : 600, fontSize: 14, color: "var(--text-1)" }}>
                        {n.title}
                      </span>
                      {!n.read && <div style={{ width: 8, height: 8, borderRadius: "50%", background: "var(--accent)", flexShrink: 0 }} />}
                    </div>
                    {n.message && (
                      <div style={{
                        fontSize: 12, color: "var(--text-3)", marginTop: 2,
                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                      }}>
                        {n.message}
                      </div>
                    )}
                  </div>
                  <div style={{ fontSize: 11, color: "var(--text-3)", flexShrink: 0, marginTop: 2 }}>
                    {timeAgo(n.createdAt)}
                  </div>
                </div>
              );
            })
          )}
          {displayedNotifs.length > 0 && (
            <Pagination page={page} total={displayedNotifs.length} pageSize={PAGE_SIZE} onChange={setPage} />
          )}
        </div>
      </div>

      {/* ── Popup de détail ── */}
      {viewNotif && (
        <NotificationDetailModal
          notif={viewNotif}
          onClose={() => setViewNotif(null)}
        />
      )}
    </>
  );
}